'use client';

import React from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { RsvpQuestion, RSVP_QUESTION_TYPES } from '@/lib/schemas/event';

interface RsvpResponseFormProps {
	questions: RsvpQuestion[];
	onResponsesChange: (responses: Record<string, string>) => void;
}

export function RsvpResponseForm({
	questions,
	onResponsesChange,
}: RsvpResponseFormProps) {
	// Create dynamic schema based on questions
	const createResponseSchema = (questions: RsvpQuestion[]) => {
		const schemaFields: Record<string, z.ZodTypeAny> = {};

		questions.forEach((question) => {
			if (question.id) {
				let fieldSchema: z.ZodTypeAny = z.string();

				if (question.required) {
					if (question.type === RSVP_QUESTION_TYPES.MULTIPLE_CHOICE) {
						// For multiple choice, check that the JSON array has at least one item
						fieldSchema = z.string().refine(
							(val) => {
								if (!val) return false;
								try {
									const parsed = JSON.parse(val);
									return Array.isArray(parsed) && parsed.length > 0;
								} catch {
									return false;
								}
							},
							{ message: 'Please select at least one option' }
						);
					} else {
						fieldSchema = z
							.string()
							.min(1, { message: 'This field is required' });
					}
				} else {
					fieldSchema = z.string().optional().or(z.literal(''));
				}

				schemaFields[question.id] = fieldSchema;
			}
		});

		return z.object(schemaFields);
	};

	const responseSchema = createResponseSchema(questions);
	type ResponseFormData = z.infer<typeof responseSchema>;

	const form = useForm<ResponseFormData>({
		resolver: zodResolver(responseSchema),
		defaultValues: questions.reduce(
			(acc, question) => {
				if (question.id) {
					acc[question.id] = '';
				}
				return acc;
			},
			{} as Record<string, string>
		),
	});

	// Watch form values and notify parent
	React.useEffect(() => {
		const subscription = form.watch((values) => {
			onResponsesChange(values as Record<string, string>);
		});
		return () => subscription.unsubscribe();
	}, [form, onResponsesChange]);

	const renderQuestionField = (question: RsvpQuestion, index: number) => {
		if (!question.id) return null;

		const fieldName = question.id as keyof ResponseFormData;

		switch (question.type) {
			case RSVP_QUESTION_TYPES.TEXT:
				return (
					<FormField
						key={question.id}
						control={form.control}
						name={fieldName}
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{question.question}
									{question.required && (
										<span className='text-red-500 ml-1'>*</span>
									)}
								</FormLabel>
								<FormControl>
									<Input {...field} placeholder='Type your answer...' />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				);

			case RSVP_QUESTION_TYPES.TEXTAREA:
				return (
					<FormField
						key={question.id}
						control={form.control}
						name={fieldName}
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{question.question}
									{question.required && (
										<span className='text-red-500 ml-1'>*</span>
									)}
								</FormLabel>
								<FormControl>
									<Textarea {...field} placeholder='Type your answer...' />
								</FormControl>
								<FormMessage />
							</FormItem>
						)}
					/>
				);

			case RSVP_QUESTION_TYPES.SELECT:
				return (
					<FormField
						key={question.id}
						control={form.control}
						name={fieldName}
						render={({ field }) => (
							<FormItem>
								<FormLabel>
									{question.question}
									{question.required && (
										<span className='text-red-500 ml-1'>*</span>
									)}
								</FormLabel>
								<Select
									onValueChange={field.onChange}
									defaultValue={field.value}
								>
									<FormControl>
										<SelectTrigger>
											<SelectValue placeholder='Select an option' />
										</SelectTrigger>
									</FormControl>
									<SelectContent>
										{question.options?.map((option, optionIndex) => (
											<SelectItem key={optionIndex} value={option}>
												{option}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
								<FormMessage />
							</FormItem>
						)}
					/>
				);

			case RSVP_QUESTION_TYPES.MULTIPLE_CHOICE:
				return (
					<FormField
						key={question.id}
						control={form.control}
						name={fieldName}
						render={({ field }) => {
							// Parse current selected values from JSON string or use empty array
							let selectedValues: string[] = [];
							try {
								if (field.value) {
									const parsed = JSON.parse(field.value);
									if (Array.isArray(parsed)) {
										selectedValues = parsed;
									}
								}
							} catch {
								// If not valid JSON, treat as empty (legacy handling)
								selectedValues = [];
							}

							return (
								<FormItem>
									<FormLabel>
										{question.question}
										{question.required && (
											<span className='text-red-500 ml-1'>*</span>
										)}
									</FormLabel>
									<div className='space-y-3'>
										{question.options?.map((option, optionIndex) => {
											const checkboxId = `rsvp-${question.id}-opt-${optionIndex}`;
											const normalizedOption = option.trim();
											const isChecked = selectedValues.includes(normalizedOption);
											
											const handleChange = (checked: boolean) => {
												let newValues: string[];
												if (checked) {
													// Add option if not already present
													if (!selectedValues.includes(normalizedOption)) {
														newValues = [...selectedValues, normalizedOption];
													} else {
														newValues = selectedValues;
													}
												} else {
													// Remove option
													newValues = selectedValues.filter(v => v !== normalizedOption);
												}
												
												// Store as JSON string
												const newValue = JSON.stringify(newValues);
												field.onChange(newValue);
											};

											return (
												<div
													key={`${question.id}-opt-${optionIndex}`}
													className='flex items-center gap-3'
												>
													<Checkbox
														id={checkboxId}
														checked={isChecked}
														onCheckedChange={handleChange}
													/>
													<Label
														htmlFor={checkboxId}
														className='text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer select-none flex-1'
														onClick={(e) => {
															e.preventDefault();
															e.stopPropagation();
															handleChange(!isChecked);
														}}
													>
														{option}
													</Label>
												</div>
											);
										})}
									</div>
									<FormMessage />
								</FormItem>
							);
						}}
					/>
				);

			case RSVP_QUESTION_TYPES.BOOLEAN:
				return (
					<FormField
						key={question.id}
						control={form.control}
						name={fieldName}
						render={({ field }) => (
							<FormItem className='flex flex-row items-center justify-between rounded-lg border p-4'>
								<div className='space-y-0.5'>
									<FormLabel>
										{question.question}
										{question.required && (
											<span className='text-red-500 ml-1'>*</span>
										)}
									</FormLabel>
								</div>
								<FormControl>
									<Switch
										checked={field.value === 'true'}
										onCheckedChange={(checked) =>
											field.onChange(checked ? 'true' : 'false')
										}
									/>
								</FormControl>
							</FormItem>
						)}
					/>
				);

			case RSVP_QUESTION_TYPES.SECTION_HEADER:
				return (
					<div key={question.id} className='space-y-3 py-6 first:pt-3'>
						<div className='border-b-2 border-primary/20 pb-3'>
							<h3 className='text-xl font-bold text-foreground mb-1'>
								{question.question}
							</h3>
							{question.description && (
								<p className='text-base text-muted-foreground leading-relaxed'>
									{question.description}
								</p>
							)}
						</div>
					</div>
				);

			default:
				return null;
		}
	};

	if (questions.length === 0) {
		return null;
	}

	return (
		<Form {...form}>
			<div className='space-y-4'>
				<div>
					<h3 className='text-lg font-semibold'>Additional Information</h3>
					<p className='text-sm text-muted-foreground'>
						Please provide the following information for your ticket purchase.
						{questions.filter((q) => q.required).length > 0 && (
							<span className='text-red-500 ml-1'>
								({questions.filter((q) => q.required).length} required)
							</span>
						)}
					</p>
				</div>
				{questions.map((question, index) =>
					renderQuestionField(question, index)
				)}
			</div>
		</Form>
	);
}
